import { useEffect, useMemo, useState } from 'react';
import { AsYouType, getCountries, getCountryCallingCode, parsePhoneNumberFromString } from 'libphonenumber-js';
import { Check, ChevronsUpDown } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';

const digitsOnly = (value) => String(value || '').replace(/\D/g, '');

const displayNames = new Intl.DisplayNames(['it'], { type: 'region' });
const PHONE_PREFIXES = getCountries()
  .map((country) => ({ country, callingCode: `+${getCountryCallingCode(country)}`, name: displayNames.of(country) || country }))
  .sort((left, right) => left.name.localeCompare(right.name, 'it'));

const countryForPrefix = (value, fallback = 'CH') => {
  const parsed = parsePhoneNumberFromString(String(value || '').replace(/^00/, '+'));
  return parsed?.country || fallback;
};

export const formatPublicPhoneNumber = (value, country = 'CH') => {
  const digits = digitsOnly(value);
  if (!digits) return '';
  const formatted = new AsYouType(country).input(digits);
  return formatted || digits;
};

export const formatPublicPhonePrefix = (value) => {
  const digits = digitsOnly(value);
  return digits ? `+${digits}` : '';
};

export const publicPhoneValue = (prefix, number) => {
  const formattedPrefix = formatPublicPhonePrefix(prefix);
  const formattedNumber = formatPublicPhoneNumber(number);
  return [formattedPrefix, formattedNumber].filter(Boolean).join(' ');
};

export const formatIban = (value) => String(value || '')
  .toUpperCase()
  .replace(/[^A-Z0-9]/g, '')
  .replace(/(.{4})/g, '$1 ')
  .trim();

export function PublicPhoneInput({
  id,
  label,
  prefix = '+41',
  number = '',
  onPrefixChange,
  onNumberChange,
  required = false,
  optional = false,
  lang = 'it',
  autoComplete = 'tel',
  prefixName,
  numberName,
  testId,
}) {
  const initialCountry = countryForPrefix(prefix);
  const [country, setCountry] = useState(initialCountry);
  const [open, setOpen] = useState(false);
  const selected = useMemo(() => PHONE_PREFIXES.find((item) => item.country === country) || PHONE_PREFIXES.find((item) => item.country === 'CH'), [country]);

  useEffect(() => setCountry(countryForPrefix(prefix, country)), [prefix]);

  const emit = (nextCountry, rawNumber) => {
    const formatted = formatPublicPhoneNumber(rawNumber, nextCountry);
    setCountry(nextCountry);
    onPrefixChange(`+${getCountryCallingCode(nextCountry)}`);
    onNumberChange(formatted);
  };

  return (
    <div>
      <Label htmlFor={`${id}-prefix`} className="mb-1.5 block text-sm font-bold">
        {label}{optional && <span className="font-normal text-muted-foreground"> ({lang === 'en' ? 'optional' : 'Facoltativo'})</span>}
      </Label>
      <div className="flex h-11 overflow-hidden rounded-lg border border-input bg-transparent focus-within:ring-2 focus-within:ring-ring">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button type="button" role="combobox" aria-expanded={open} aria-label={lang === 'en' ? 'International prefix' : 'Prefisso internazionale'} className={cn('flex h-full w-20 shrink-0 items-center justify-between border-r border-input px-3 text-left text-sm outline-none hover:bg-muted/40')} data-testid={`${testId || id}-prefix-select`}>
              <span className="truncate tabular-nums">{selected.callingCode}</span><ChevronsUpDown className="ml-1 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-0" align="start">
            <Command>
              <CommandInput placeholder={lang === 'en' ? 'Search country or prefix…' : 'Cerca paese o prefisso…'} />
              <CommandList className="max-h-72 overflow-y-auto overscroll-contain touch-pan-y">
                <CommandEmpty>{lang === 'en' ? 'No prefix found.' : 'Nessun prefisso trovato.'}</CommandEmpty>
                <CommandGroup>{PHONE_PREFIXES.map((item) => <CommandItem key={item.country} value={`${item.name} ${item.country} ${item.callingCode}`} onSelect={() => { emit(item.country, number); setOpen(false); }}><Check className={cn('h-4 w-4', country === item.country ? 'opacity-100' : 'opacity-0')} /><span className="min-w-0 flex-1 truncate">{item.name}</span><span className="tabular-nums text-muted-foreground">{item.callingCode}</span></CommandItem>)}</CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <input type="hidden" name={prefixName} value={formatPublicPhonePrefix(prefix)} />
        <Input
          id={id}
          name={numberName}
          data-testid={testId}
          type="tel"
          inputMode="tel"
          required={required}
          value={number}
          onChange={(event) => emit(country, event.target.value)}
          autoComplete={autoComplete}
          placeholder={lang === 'en' ? 'Phone number' : 'Numero di telefono'}
          aria-label={`${label} senza prefisso`}
          className="h-full flex-1 border-0 bg-transparent px-2 tabular-nums shadow-none focus-visible:ring-0"
        />
      </div>
    </div>
  );
}
